import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { getProducts, fromWcProduct } from '../../../lib/wp-api';

// One representative product per fashion category. Each card links to the
// product-details page and shows image + name + price on hover.
const PICKS = [
  { categoryId: 16, label: 'Dresses' },
  { categoryId: 41, label: 'Swimwear' },
  { categoryId: 50, label: 'Activewear' },
  { categoryId: 53, label: 'Jeans' },
];

const SocialProof = () => {
  const [picks, setPicks] = useState([]);

  useEffect(() => {
    let cancelled = false;
    // Fetch one product per category in parallel — server-side category
    // filter so we don't need the whole catalog in memory.
    Promise.all(
      PICKS.map((p) =>
        getProducts({ per_page: 1, category: p.categoryId, orderby: 'popularity' })
          .then((rows) => (rows && rows[0] ? { ...p, product: fromWcProduct(rows[0]) } : { ...p, product: null }))
          .catch(() => ({ ...p, product: null }))
      )
    ).then((rows) => { if (!cancelled) setPicks(rows.filter((r) => r.product)); });
    return () => { cancelled = true; };
  }, []);

  if (picks.length === 0) return null; // gracefully hide if WC returns nothing

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
            Styles the Community Is Raving About
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Real pieces our shoppers can't stop wearing — one from each section of the shop.
          </motion.p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {picks.map((p, index) => (
            <motion.div
              key={p.product.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.03 }}
              className="relative group overflow-hidden rounded-xl"
            >
              <Link to={`/product-details?id=${p.product.id}`} className="block">
                <img
                  src={p.product.image}
                  alt={p.product.imageAlt || p.product.name}
                  className="w-full aspect-square object-cover"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium shadow">
                  {p.label}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="text-white font-medium line-clamp-2 text-sm mb-1">{p.product.name}</p>
                    <div className="flex items-center justify-between text-white text-sm">
                      <span>${(p.product.price ?? 0).toFixed(2)}</span>
                      <span className="inline-flex items-center gap-1 opacity-90">
                        Shop
                        <Icon name="ArrowRight" size={14} />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialProof;
